// src/v2/pulse/LeaseWindowsCalPane.jsx
//
// Calendar-card lease action windows pane for PulsePage.
// Accepts the derived lease array (output of deriveLeaseActions) as `derivedLeases`,
// filters out non-actionable watch/expired buckets, sorts by bucket priority then
// days_to_next_action, shows the top 3 by default with an expand/collapse toggle.
//
// Design: docs/p23-lease-action-windows-comp.html (P-23 redesign)
// One urgency band per row (r/a/b) drives the calendar strip, pill label,
// and countdown colour — all four visual elements always agree.

import { useState } from "react";
import { T } from "../../app/tokens.js";
import { resolveTimelineEndAnchor } from "../../features/leases/leaseTabConfig.js";
import { PULSE_PANE_H, OUTBOUND_LINK_STYLE } from "./pulseConstants.js";
import { PaneInfoTooltip } from "./PaneInfoTooltip.jsx";
import { SlideSheet } from "../../components/SlideSheet.jsx";
import { OfficeNameLabel } from "../../components/OfficeNameLabel.jsx";
import { RenewalDecisionModal } from "../leases/RenewalDecisionModal.jsx";
import { InternalLink } from "../../components/InternalLink.jsx";
import { buildLeaseUrl } from "../../services/buildUrl.js";

/* ─── Band logic ─────────────────────────────────────────────────────────── */

/**
 * Maps an action_window_bucket to an urgency band letter.
 * @param {'decide-now'|'negotiate'|'reduce-exposure'|'monitor'|'expired'} bucket
 * @returns {'r' | 'a' | 'y' | 'b'}
 */
export function getBand(bucket) {
  if (bucket === "decide-now") return "r";
  if (bucket === "negotiate") return "a";
  if (bucket === "reduce-exposure") return "y";
  return "b";
}

/* ─── Bucket priority order (for sorting) ───────────────────────────────── */

const BUCKET_PRIORITY = { "decide-now": 0, "negotiate": 1, "reduce-exposure": 2, "monitor": 3 };

// Home is an action surface. Monitor remains available from the shared
// selector and the full Leases board, but is intentionally omitted here.
const HOME_ACTION_BUCKETS = new Set(["decide-now", "negotiate", "reduce-exposure"]);

/* ─── Band-driven constants ──────────────────────────────────────────────── */

// Solid-soft hex colours for the calendar tile month band only.
const BAND_BUCKET = {
  r: "decide-now",
  a: "negotiate",
  y: "reduce-exposure",
  b: "monitor",
};

const BAND_COLORS = {
  r: T.leaseActionBuckets["decide-now"],
  a: T.leaseActionBuckets.negotiate,
  y: T.leaseActionBuckets["reduce-exposure"],
  b: T.leaseActionBuckets.monitor,
};

/**
 * Returns the pill label for a given action_window_bucket.
 * @param {string} bucket
 * @returns {string}
 */
function bucketPillLabel(bucket) {
  if (bucket === "decide-now") return "Next 90 days";
  if (bucket === "negotiate")  return "Within a year";
  if (bucket === "reduce-exposure") return "Reduce exposure";
  if (bucket === "expired") return "Expired";
  return "Monitor";
}

const MONTH_ABBR = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const CAL_TILE_HEIGHT = 44;

/* ─── Helpers ────────────────────────────────────────────────────────────── */

function calFromDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T12:00:00Z");
  return {
    mo: MONTH_ABBR[d.getUTCMonth()],
    dy: d.getUTCDate(),
  };
}

// Use the timeline's existing anchor, not the (intentionally null) next action
// date. Read calendar components directly so UTC-midnight API timestamps do not
// shift to the preceding day for users west of UTC.
function pastAnchorCal(lease) {
  const value = resolveTimelineEndAnchor(lease).date;
  if (typeof value !== "string") return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:$|T)/.exec(value);
  if (!match) return null;
  if (value.includes("T") && !Number.isFinite(Date.parse(value))) return null;
  const [, year, month, day] = match.map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  const today = new Date();
  const localToday = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  if (date.getTime() > localToday) return null;
  return { mo: MONTH_ABBR[month - 1], dy: day };
}

function formatRent(annualRentUsd) {
  if (annualRentUsd == null) return "—";
  const n = Number(annualRentUsd);
  if (Number.isNaN(n)) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M/yr`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(0)}k/yr`;
  return `$${n.toLocaleString()}/yr`;
}

/* ─── Sub-component: calendar tile ──────────────────────────────────────── */

function CalTile({ cal, band }) {
  const colors = BAND_COLORS[band];
  return (
    <div style={{
      width: 38, height: CAL_TILE_HEIGHT, boxSizing: "border-box", borderRadius: 10, overflow: "hidden",
      border: "1px solid var(--v2-line)", background: "var(--v2-card)",
      textAlign: "center", flexShrink: 0,
    }}>
      {/* Calendar month/past labels use the canonical 11px small-label size. */}
      <div style={{
        height: 14, display: "grid", placeItems: "center",
         background: colors.tint,
        fontSize: T.fsXs, fontWeight: T.fwBold,
        letterSpacing: ".10em", textTransform: "uppercase",
         color: colors.foreground,
      }}>
        {cal.mo}
      </div>
      <div style={{
        fontSize: T.fsLg, fontWeight: T.fwDisplay, letterSpacing: "-.03em",
        paddingTop: 3, paddingBottom: 3, color: "var(--v2-dark)",
      }}>
        {cal.dy}
      </div>
    </div>
  );
}

function OverdueCalTile({ cal, id }) {
  return (
    <div
      data-testid={`v2-lease-window-overdue-chip-${id}`}
      aria-label={cal ? `Expired ${cal.mo} ${cal.dy}` : "Overdue; expiration date unavailable"}
      style={{
        width: 38, height: CAL_TILE_HEIGHT, boxSizing: "border-box", flexShrink: 0,
        borderRadius: 10, border: `1px solid ${T.ragRedBorder}`,
        background: T.ragRedBg, color: T.ragRed,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        textAlign: "center",
      }}
    >
      {cal ? (
        <>
          <span style={{ fontSize: T.fsXs, fontWeight: T.fwBold, lineHeight: 1, textTransform: "uppercase" }}>{cal.mo}</span>
          <span style={{ fontSize: T.fsLg, fontWeight: T.fwDisplay, lineHeight: 1.05 }}>{cal.dy}</span>
          <span style={{ fontSize: T.fsXs, fontWeight: T.fwBold, lineHeight: 1, textTransform: "uppercase" }}>past</span>
        </>
      ) : (
        <span aria-hidden="true" style={{ fontSize: T.fsLgX, fontWeight: T.fwBold, lineHeight: 1 }}>!</span>
      )}
    </div>
  );
}

/* ─── Sub-component: one calendar-card row ───────────────────────────────── */

function WindowCalRow({ lease, onClick, isFirst }) {
  const band   = getBand(lease.action_window_bucket);
  const colors = BAND_COLORS[band];
  const color  = colors.foreground;
  const cal    = calFromDate(lease.next_action_date);
  const isOverdue = !lease.next_action_date && lease.posture === "holdover";
  const pastCal = isOverdue ? pastAnchorCal(lease) : null;
  const city   = lease.city || lease.locationMarket || "";
  const market = lease.locationMarket || "";
  const alias  = lease.locationAlias  || "";
  const label  = lease.action_short_reason || "";
  const rent   = formatRent(lease.annualRentUsd);
  const days   = lease.days_to_next_action;

  // P-23: sub-label appends market — `$123k/yr · Lease expiration · Dubai`
  const subLabel = `${rent}${label ? ` · ${label}` : ""}${market ? ` · ${market}` : ""}${pastCal ? ` · expired ${pastCal.mo} ${pastCal.dy}` : ""}`;
  const rowPillLabel = lease.action_window_bucket === "reduce-exposure"
    ? (lease.posture === "winding-down" ? "Winding-down" : "Sublease")
    : bucketPillLabel(lease.action_window_bucket);

  const countdownLabel = days != null
    ? `${days} days remaining`
    : lease.posture === "holdover"
      ? "Overdue"
      : "—";
  const countdownStyle = { fontSize: T.fsXs, fontWeight: T.fwBold, color, whiteSpace: "nowrap" };

  return (
    <div
      data-testid={`v2-lease-window-row-${lease.id}`}
      onClick={onClick}
      style={{
        display: "grid",
        gridTemplateColumns: "38px minmax(0,1fr) auto",
        alignItems: "center",
        gap: 10,
        padding: "5px 10px",
        borderTop: isFirst ? "none" : "1px solid var(--v2-hair)",
        cursor: "pointer",
        transition: "background .12s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "var(--v2-tint)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "none";
      }}
    >
      {/* Calendar tile */}
      {cal ? (
        <CalTile cal={cal} band={band} />
      ) : isOverdue ? (
        <OverdueCalTile cal={pastCal} id={lease.id} />
      ) : (
        <div
          data-testid={`v2-lease-window-no-date-slot-${lease.id}`}
          aria-label="No upcoming action date"
          style={{
            width: 38, height: 38, display: "grid", placeItems: "center",
            border: "1px solid var(--v2-line)", borderRadius: 10,
            background: "var(--v2-tint)", color: "var(--v2-mid)",
            fontSize: T.fsLg, fontWeight: T.fwBold, flexShrink: 0,
          }}
        >
          <span aria-hidden="true">·</span>
        </div>
      )}

      {/* Middle: title + rent · label · market */}
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontSize: T.fsBase, lineHeight: 1.2,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          <OfficeNameLabel city={city} alias={alias} locationId={lease.locationId} />
        </div>
        <div style={{
          fontSize: T.fsBase, color: "var(--v2-mid)", fontWeight: T.fwRegular, marginTop: 1,
        }}>
          {subLabel}
        </div>
      </div>

      {/* Right: pill + countdown */}
      <div style={{ display: "grid", justifyItems: "end", gap: 4 }}>
        <span style={{
          fontSize: T.fsXs, fontWeight: T.fwSemibold,
          padding: "2px 10px", borderRadius: 6,
           background: colors.tint, color,
          whiteSpace: "nowrap",
        }}>
          {rowPillLabel}
        </span>
        <span style={countdownStyle}>
          {countdownLabel}
        </span>
      </div>
    </div>
  );
}

/* ─── Main pane component ────────────────────────────────────────────────── */

/**
 * @param {{ derivedLeases: Array, onNavigate?: (url: string) => void, twoUp?: boolean }} props
 */
export function LeaseWindowsCalPane({ derivedLeases, onNavigate, twoUp = false }) {
  const [selected,         setSelected]         = useState(null);
  const [decisionLease,    setDecisionLease]    = useState(null);

  // The selector is the source of truth. Keep Monitor and Expired out of the
  // Home nearest-action list.
  const actionLeases = (Array.isArray(derivedLeases) ? derivedLeases : [])
    .filter((l) => HOME_ACTION_BUCKETS.has(l.action_window_bucket))
    .slice()
    .sort((a, b) => {
      const pa = BUCKET_PRIORITY[a.action_window_bucket] ?? 99;
      const pb = BUCKET_PRIORITY[b.action_window_bucket] ?? 99;
      if (pa !== pb) return pa - pb;
      const da = a.days_to_next_action;
      const db = b.days_to_next_action;
      if (da === null && db === null) return 0;
      if (da === null) return 1;
      if (db === null) return -1;
      return da - db;
    });

  const VISIBLE_CAP = 3;
  const windows = actionLeases.slice(0, VISIBLE_CAP);

  // P-23: Bucket hero card data — tinted cards with count + dollar exposure
  const BUCKET_STRIP = [
    {
      key: "decide-now",
      label: "Next 90 days",
       accentColor: T.leaseActionBuckets["decide-now"].foreground,
       bg: T.leaseActionBuckets["decide-now"].tint,
       borderColor: T.leaseActionBuckets["decide-now"].tint,
      testId: "v2-aw-bucket-decide",
    },
    {
      key: "negotiate",
      label: "Within a year",
       accentColor: T.leaseActionBuckets.negotiate.foreground,
       bg: T.leaseActionBuckets.negotiate.tint,
       borderColor: T.leaseActionBuckets.negotiate.tint,
      testId: "v2-aw-bucket-negotiate",
    },
    {
      key: "reduce-exposure",
      label: "Reduce exposure",
       accentColor: T.leaseActionBuckets["reduce-exposure"].foreground,
       bg: T.leaseActionBuckets["reduce-exposure"].tint,
       borderColor: T.leaseActionBuckets["reduce-exposure"].tint,
      testId: "v2-aw-bucket-reduce-exposure",
    },
  ];
  const bucketStats = BUCKET_STRIP.map(({ key, label, accentColor, bg, borderColor, testId }) => {
    const matching = actionLeases.filter((l) => l.action_window_bucket === key);
    const count = matching.length;
    const totalRent = matching
      .filter((l) => key !== "reduce-exposure" || l.posture === "sublease")
      .reduce((sum, l) => sum + (Number(l.annualRentUsd) || 0), 0);
    return { key, label, accentColor, bg, borderColor, testId, count, totalRent };
  }).filter((stat) => stat.count > 0);

  const lwTooltip = `Leases ordered by urgency: Next 90 days, Within a year, Reduce exposure.`;

  return (
    <>
      <div
        data-testid="v2-section-lease-actions"
        style={{
          background: "var(--v2-card)",
          border: "1px solid var(--v2-line)",
          borderRadius: 16,
          boxShadow: "var(--v2-shadow-card)",
          padding: "18px 18px 14px",
          minHeight: PULSE_PANE_H,
          height: twoUp ? "100%" : undefined,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div data-testid="v2-lease-actions-title" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: T.fsBase, fontWeight: T.fwBold }}>
            <span style={{ display: "inline-block", width: 3, height: 14, background: "var(--v2-accent)", borderRadius: 2 }} />
            Lease action windows
            <PaneInfoTooltip
              data-testid="v2-lease-windows-tooltip"
              text={lwTooltip}
            />
          </div>
        </div>

        {/* P-23: Bucket hero cards — tinted, with count + dollar exposure */}
        <div
          data-testid="v2-aw-bucket-strip"
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${Math.max(1, Math.min(3, bucketStats.length))}, minmax(0, 1fr))`,
            gap: 8,
            marginBottom: 0,
          }}
        >
          {bucketStats.map(({ key, label, accentColor, bg, borderColor, testId, count, totalRent }) => {
            return (
              <div
                key={testId}
                data-testid={testId}
                style={{
                  background: bg,
                  border: `1px solid ${borderColor}`,
                  borderRadius: 10,
                  padding: "9px 8px",
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                {/* Option A: full-width label followed by a compact financial summary. */}
                <div style={{
                  display: "block",
                  fontSize: T.fsXs, fontWeight: T.fwBold,
                  letterSpacing: ".02em", textTransform: "uppercase",
                  color: accentColor,
                  overflow: "hidden",
                  textOverflow: "clip",
                  whiteSpace: "nowrap",
                }}>
                  {label}
                </div>
                {/* Dollar stays intact; only the count may ellipsize when space is tight. */}
                <div style={{
                  display: "flex", alignItems: "baseline", minWidth: 0,
                  marginTop: 4, whiteSpace: "nowrap",
                }}>
                  <span style={{
                    fontSize: T.fsLg, fontWeight: T.fwDisplay,
                    color: accentColor, flexShrink: 0, whiteSpace: "nowrap",
                  }}>
                    {formatRent(totalRent)}
                  </span>
                  <span
                    data-testid={`${testId}-count`}
                    style={{
                      minWidth: 0, marginLeft: 5,
                      fontSize: T.fsXs, fontWeight: T.fwSemibold,
                      color: "var(--v2-mid)",
                      overflow: "hidden", textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {`· ${count} leases`}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* P-23: "Nearest windows" section divider */}
        <div
          data-testid="v2-aw-nearest-label"
          style={{
            fontSize: T.fsXs,
            letterSpacing: ".05em",
            textTransform: "uppercase",
            color: T.mid,
            fontWeight: T.fwBold,
            borderTop: "1px solid var(--v2-hair)",
            padding: "6px 0 6px",
            marginTop: 10,
          }}
        >
          Nearest windows
        </div>

        {/* Divided list rows — no grid gap, hairline dividers between rows */}
        <div>
          {windows.length === 0 ? (
            <div style={{
              display: "grid", placeItems: "center", minHeight: 100,
              border: "1.5px dashed var(--v2-line)", borderRadius: 12,
              color: "var(--v2-light)", fontSize: T.fsBase, fontStyle: "italic",
            }}>
              No active lease action windows
            </div>
          ) : (
            windows.map((w, i) => (
              <WindowCalRow key={w.id} lease={w} onClick={() => onNavigate?.(`?view=leases&leaseView=timeline&focus=${encodeURIComponent(w.id)}`)} isFirst={i === 0} />
            ))
          )}
        </div>

        {/* Footer: "View all" link only */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginTop: 13 }}>
          <button
            data-testid="v2-lease-windows-view-all"
            onClick={() => { if (onNavigate) onNavigate("?view=leases&leaseView=timeline"); }}
            style={{ ...OUTBOUND_LINK_STYLE }}
          >
            View all on the Leases board{" "}
            <span style={{ color: "var(--v2-accent)" }}>→</span>
          </button>
        </div>
      </div>

      {/* Lease detail drawer */}
      <SlideSheet
        open={selected !== null}
        onClose={() => setSelected(null)}
        width={420}
        scrimTestId="v2-lease-window-scrim"
        panelTestId="v2-lease-window-panel"
      >
        {selected && (() => {
          const band  = getBand(selected.action_window_bucket);
          const colors = BAND_COLORS[band];
          const color = colors.foreground;
          const bg    = colors.tint;
          const days  = selected.days_to_next_action;
          return (
            <div style={{
              height: "100%", display: "flex", flexDirection: "column", overflow: "hidden",
            }}>
              <div style={{ padding: "18px 20px", borderBottom: "1px solid var(--v2-line)" }}>
                <div style={{ fontSize: T.fsLg, fontWeight: T.fwBold }}>Lease action window</div>
              </div>
              <div style={{ flex: 1, overflow: "auto", padding: "20px" }}>
                <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", fontSize: T.fsXs, fontWeight: T.fwSemibold, padding: "2px 10px", borderRadius: 6, background: bg, color }}>
                    {days != null ? `${days}d left` : "—"}
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", fontSize: T.fsXs, fontWeight: T.fwSemibold, padding: "2px 10px", borderRadius: 6, background: bg, color }}>
                    {bucketPillLabel(selected.action_window_bucket)}
                  </span>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: T.fsXs, fontWeight: T.fwBold, letterSpacing: ".05em", textTransform: "uppercase", color: T.mid, marginBottom: 4 }}>Office</div>
                  <div style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: "var(--v2-dark)" }}>
                    {selected.locationAlias || selected.locationMarket || "—"}
                  </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: T.fsXs, fontWeight: T.fwBold, letterSpacing: ".05em", textTransform: "uppercase", color: T.mid, marginBottom: 4 }}>Action</div>
                  <div style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color }}>{selected.action_short_reason || "—"}</div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: T.fsXs, fontWeight: T.fwBold, letterSpacing: ".05em", textTransform: "uppercase", color: T.mid, marginBottom: 4 }}>Due</div>
                  <div style={{ fontSize: T.fsBase, color: "var(--v2-dark)" }}>
                    {selected.next_action_date
                      ? new Date(selected.next_action_date + "T12:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" })
                      : "—"}
                  </div>
                </div>
              </div>
              <div style={{ padding: "14px 20px", borderTop: "1px solid var(--v2-line)", display: "flex", flexDirection: "column", gap: 8 }}>
                {/* P-15: "Start renewal decision" CTA — only for decide-now/negotiate
                    buckets and only when the action window is within 5 years (≤1825d). */}
                {(selected.action_window_bucket === "decide-now" || selected.action_window_bucket === "negotiate") &&
                  (selected.days_to_next_action == null || selected.days_to_next_action <= 1825) && (
                  <button
                    type="button"
                    data-testid="v2-lease-window-start-decision"
                    onClick={() => setDecisionLease(selected)}
                    style={{
                      display: "block", width: "100%", textAlign: "center",
                      background: "var(--v2-blue-tint)", color: "var(--v2-accent)",
                      border: "1px solid var(--v2-blue-border)",
                      borderRadius: 10, padding: "9px 0",
                      fontSize: T.fsBase, fontWeight: T.fwBold,
                      cursor: "pointer",
                      transition: "opacity .15s var(--v2-ease)",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.80"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
                  >
                    Start renewal decision →
                  </button>
                )}
                <InternalLink
                  data-testid="v2-lease-window-open-link"
                  href={buildLeaseUrl(selected.leaseId)}
                  style={{
                    display: "block", textAlign: "center",
                    background: "var(--v2-accent)", color: T.white,
                    borderRadius: 10, padding: "10px 0",
                    fontSize: T.fsBase, fontWeight: T.fwBold,
                    textDecoration: "none",
                    transition: "opacity .15s var(--v2-ease)",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.88"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
                >
                  Open lease →
                </InternalLink>
              </div>
            </div>
          );
        })()}
      </SlideSheet>
      {/* P-15: Renewal decision modal — portal overlay */}
      {decisionLease && (
        <RenewalDecisionModal
          lease={{
            leaseId:              decisionLease.leaseId,
            locationId:           decisionLease.locationId,
            locationAlias:        decisionLease.locationAlias,
            locationMarket:       decisionLease.locationMarket,
            endDate:              decisionLease.next_action_date ?? null,
            days_to_next_action:  decisionLease.days_to_next_action,
            action_window_bucket: decisionLease.action_window_bucket,
          }}
          criticalDates={[]}
          onClose={() => setDecisionLease(null)}
          onLogged={() => setDecisionLease(null)}
        />
      )}
    </>
  );
}
